import React, { Dispatch, SetStateAction } from 'react'
import {
  Row,
  Modal,
  Col,
  Form,
  Button,
  DatePicker
} from 'react-angle-dashboard-components'
import { useFormContext, useWatch, useForm, Controller } from 'react-hook-form'
import { censusMemberSchema } from './schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import { ICensusForm } from './types'
import { ModalFooter } from './ModalFooter'
import { InvalidFeedback } from '../../style'
import { DateHandler } from '@angle/utils'

export const AddOrEditMemberModal: React.FC<{
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  rowIdx?: number
  defaultValues?: ICensusForm
}> = ({ show, setShow, rowIdx, defaultValues = {} }) => {
  // top level new quote form
  const { setValue: setValueNewQuote } = useFormContext()
  const census = useWatch({ name: 'censusInfo' })

  // check if adding or editing member
  const hasValidRowIdx = typeof rowIdx === 'number' && census.length > rowIdx

  const modalTitle = hasValidRowIdx ? 'Edit Member' : 'Add Member'

  // add or edit member sub-form
  const formMethods = useForm<ICensusForm>({
    resolver: zodResolver(censusMemberSchema),
    mode: 'all',
    defaultValues
  })

  const { register, handleSubmit, reset, control, formState } = formMethods

  const errors = formState?.errors

  const closeModal = () => {
    reset()
    setShow(false)
  }

  const onSave = handleSubmit((data: ICensusForm) => {
    const newArr = [...census]
    if (hasValidRowIdx && rowIdx !== undefined) {
      newArr[rowIdx] = data
    } else {
      newArr.push(data)
    }

    setValueNewQuote('censusInfo', newArr)
    closeModal()
  })

  return (
    <Modal
      centered
      size="lg"
      show={show}
      style={{ zIndex: 9000 }}
      onHide={() => closeModal()}
    >
      <Modal.Header className="border-bottom pb-3 pt-4" closeButton>
        <h5>{modalTitle}</h5>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col sm={12} md={6}>
            <Form.Group>
              <Form.Label>First Name</Form.Label>
              <Form.Control
                placeholder="First Name"
                className="bg-white"
                {...register('firstName')}
                isInvalid={!!errors.firstName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={12} md={6}>
            <Form.Group>
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                placeholder="Last Name"
                className="bg-white"
                {...register('lastName')}
                isInvalid={!!errors.lastName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col sm={12} md={6}>
            <Form.Group>
              <Form.Label>Date of Birth</Form.Label>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    onChange={(date) =>
                      field.onChange(DateHandler.formatForClient(date))
                    }
                  />
                )}
              />
              {!!errors.dateOfBirth && (
                <InvalidFeedback>{errors.dateOfBirth?.message}</InvalidFeedback>
              )}
            </Form.Group>
          </Col>
          <Col sm={12} md={6}>
            <Form.Group>
              <Form.Label> Zip Code</Form.Label>
              <Form.Control
                placeholder="Zip Code"
                className="bg-white"
                {...register('zipCode')}
                isInvalid={!!errors.zipCode}
              />
              <Form.Control.Feedback type="invalid">
                {errors.zipCode?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={6}>
            <Form.Group>
              <Form.Label>Relationship to Subscriber</Form.Label>
              <Form.Control
                as="select"
                className="bg-white"
                {...register('relationship')}
                isInvalid={!!errors.relationship}
              >
                <option value="" disabled>
                  Select relationship
                </option>
                <option value="employee">Subscriber</option>
                <option value="spouse">Spouse</option>
                <option value="child">Child</option>
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.relationship?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={12} md={6}>
            <Form.Group>
              <Form.Label>Gender</Form.Label>
              <Form.Control
                as="select"
                className="bg-white"
                {...register('gender')}
                isInvalid={!!errors.gender}
              >
                <option value="" disabled>
                  Select gender
                </option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.gender?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={6}>
            <Form.Group>
              <Form.Label>Tier</Form.Label>
              <Form.Control
                as="select"
                className="bg-white"
                {...register('familyTier')}
                isInvalid={!!errors.familyTier}
              >
                <option value="" disabled>
                  Select plan tier
                </option>
                <option value="single">Employee Only</option>
                <option value="employee_spouse">Employee + Spouse</option>
                <option value="employee_children">Employee + Child(ren)</option>
                <option value="family">Employee + Family</option>
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.familyTier?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={12} md={6}>
            <Form.Group>
              <Form.Label>Plan (optional)</Form.Label>
              <Form.Control
                placeholder="Previous Plan"
                className="bg-white"
                {...register('prevPlanName')}
                isInvalid={!!errors.prevPlanName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.prevPlanName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <ModalFooter>
        <Button
          className="py-2"
          variant="outline-primary"
          onClick={() => closeModal()}
        >
          Cancel
        </Button>
        <Button className="py-2" onClick={() => onSave()}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  )
}
